import React from 'react';
import { useSeoMeta } from '@unhead/react';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostr } from '@/hooks/useNostr';
import { useBookmarks } from '@/hooks/useBookmarks';
import { useQuery } from '@tanstack/react-query';
import { Tweet } from '@/components/tweet/Tweet';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent } from '@/components/ui/card';
import { Bookmark, Sparkles } from 'lucide-react';

export function BookmarksPage() {
  useSeoMeta({
    title: 'Bookmarks / Twittstr',
    description: 'Access all bookmarked microblog posts, completely saved inside secure decentralized nodes (NIP-51 kind-10003 lists).',
  });

  const { user } = useCurrentUser();
  const { nostr } = useNostr();
  const { data: bookmarkedData, isLoading: isBookmarksLoading } = useBookmarks();

  const savedIds = Array.from(bookmarkedData?.bookmarkedIds ?? []);

  // Fetch full kind 1 post items for bookmarked IDs
  const { data: bookmarkedNotes = [], isLoading: isNotesLoading } = useQuery({
    queryKey: ['bookmarkedNotes', savedIds],
    queryFn: async () => {
      if (!nostr || savedIds.length === 0) return [];
      try {
        const notes = await nostr.query([{ kinds: [1], ids: savedIds }]);
        // Sort chronological based on original bookmarks array order or created time
        return notes.sort((a, b) => b.created_at - a.created_at);
      } catch (err) {
        console.error('Failed to query bookmarked notes:', err);
        return [];
      }
    },
    enabled: !!nostr && savedIds.length > 0,
  });

  const isLoading = isBookmarksLoading || (savedIds.length > 0 && isNotesLoading);

  return (
    <div className="flex flex-col w-full min-h-screen bg-background text-foreground">
      {/* Sticky Bookmarks Header */}
      <header className="sticky top-0 bg-background/85 backdrop-blur-md z-30 border-b border-border p-4 flex flex-col leading-tight">
        <span className="font-extrabold text-xl text-foreground">Bookmarks</span>
        {user && (
          <span className="text-xs text-muted-foreground mt-0.5 select-none font-mono">
            @{user.pubkey.slice(0, 10)}...
          </span>
        )}
      </header>

      {/* Bookmarks stream list container */}
      <div className="flex flex-col divide-y divide-border">
        {!user ? (
          // Logged Out Screen
          <div className="p-8 text-center flex flex-col items-center justify-center gap-3 my-12">
            <Card className="border-dashed bg-secondary/15 w-full max-w-sm rounded-2xl py-8 shadow-none">
              <CardContent className="flex flex-col items-center justify-center text-center gap-3 p-4">
                <Bookmark className="size-8 text-sky-500" />
                <p className="font-extrabold text-[15px]">Sign in to see bookmarks</p>
                <p className="text-xs text-muted-foreground leading-normal">
                  Pills bookmarks are managed strictly on-chain using NIP-51. Authenticate with extension or secret keys to fetch and read collections.
                </p>
              </CardContent>
            </Card>
          </div>
        ) : isLoading ? (
          // Loading Skeletons
          Array.from({ length: 3 }).map((_, idx) => (
            <div key={idx} className="p-4 flex gap-3 border-b border-border animate-pulse">
              <Skeleton className="size-10 rounded-full shrink-0 animate-delay-75" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/4 rounded-md" />
                <Skeleton className="h-4 w-full rounded-md" />
              </div>
            </div>
          ))
        ) : bookmarkedNotes.length > 0 ? (
          // Render Notes Stream
          bookmarkedNotes.map((event) => (
            <Tweet key={event.id} event={event} />
          ))
        ) : (
          // Empty State
          <div className="p-12 text-center flex flex-col items-center justify-center gap-2 select-none my-12">
            <div className="size-16 rounded-full bg-secondary/30 flex items-center justify-center text-muted-foreground mb-1">
              <Bookmark className="size-8" />
            </div>
            <p className="font-extrabold text-xl">Save posts for later</p>
            <p className="text-sm text-muted-foreground leading-normal max-w-xs">
              Don’t let the good ones fly away! Tap the Bookmarks icon on any post to synchronize and save it to this direct folder.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default BookmarksPage;
